import { useState, useEffect } from "react";
import { PlexAdapter, type PlexServer } from "../../adapters/static/plex";

export interface PlexConnectStepProps {
  onConnected: (server: PlexServer, token: string, machineId: string) => void;
}

const POLL_INTERVAL_MS = 2000;
const TIMEOUT_MS = 10 * 60 * 1000;

export function PlexConnectStep({ onConnected }: PlexConnectStepProps) {
  const [pinId, setPinId] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const machineId = useState(() => crypto.randomUUID())[0];

  useEffect(() => {
    if (!pinId) return;
    const start = Date.now();
    const timer = setInterval(async () => {
      if (Date.now() - start > TIMEOUT_MS) {
        clearInterval(timer);
        setError("Plex auth timed out. Please try again.");
        setLoading(false);
        return;
      }
      try {
        const { authToken } = await PlexAdapter.pollPin(pinId, machineId);
        if (authToken) {
          clearInterval(timer);
          const { servers } = await PlexAdapter.getResources(authToken, machineId);
          if (servers.length === 0) {
            setError("No Plex servers found on this account.");
            setLoading(false);
            return;
          }
          onConnected(servers[0], authToken, machineId);
        }
      } catch (e) {
        if ((e as Error)?.name !== "AbortError") console.warn("Plex poll error:", e);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [pinId, machineId, onConnected]);

  const handleStart = async () => {
    setError(null);
    setLoading(true);
    try {
      const { pinId: newPinId, code: newCode } = await PlexAdapter.createPin(machineId);
      setPinId(newPinId);
      setCode(newCode);
      window.open(`https://plex.tv/link?pin=${encodeURIComponent(newCode)}`, "_blank", "noopener,noreferrer");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to start Plex connect");
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {!pinId && (
        <>
          <h3>Connect Plex</h3>
          <button onClick={handleStart} disabled={loading}>{loading ? "Starting..." : "Connect Plex"}</button>
        </>
      )}
      {pinId && (
        <>
          <h3>Approve on Plex</h3>
          <p>Enter this code at plex.tv/link:</p>
          <div style={{ fontSize: 24, fontWeight: "bold", letterSpacing: 4 }}>{code}</div>
          <p>Waiting for approval...</p>
        </>
      )}
      {error && <div style={{ color: "#c00", marginTop: 12 }}>{error}</div>}
    </div>
  );
}
